/**
 * SEDBUK Resolution Module
 *
 * Resolves boiler efficiency using GC number, make/model, or fallback methods.
 * Resolution order:
 * 1. GC number exact match (HIGH confidence)
 * 2. Make + model fuzzy match (MEDIUM confidence)
 * 3. Heat-loss band / age band (LOW confidence)
 * 4. Assumed default (LOW confidence)
 *
 * This replaces WHERE efficiency comes from, not HOW it's used.
 */

import sedbukData from '../../data/sedbuk/sedbuk_by_gc.json';

// ==================== TYPES ====================

export interface SedbukResolution {
  value: number;                          // Efficiency value (0-1)
  band?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';  // SEDBUK band (if from GC/model lookup)
  label: string;                          // Human-readable label
  source: "gc_lookup" | "model_lookup" | "heat_loss_band" | "assumed";
  confidence: "high" | "medium" | "low";
  matchedBoiler?: {                       // Details of matched boiler (if found)
    make: string;
    model: string;
    gcNumber: string;
    sedbuk: number;
    band: string;
  };
}

interface SedbukEntry {
  make: string;
  model: string;
  sedbuk: number;
  band: string;
  type: string;
  source: string;
}

// ==================== SEDBUK LOOKUP ====================

/**
 * Resolve SEDBUK efficiency from available data
 *
 * Resolution order:
 * 1. GC number → exact match → HIGH confidence
 * 2. Make + model → fuzzy match → MEDIUM confidence
 * 3. Heat-loss band / age band → LOW confidence
 * 4. Assumed default → LOW confidence
 *
 * @param input - Resolution input data
 * @returns SedbukResolution with efficiency and metadata
 */
export function resolveSedbuk(input: {
  gcNumber?: string;
  make?: string;
  model?: string;
  heatLossBand?: "very_efficient" | "typical" | "poor";
  ageBand?: string;
}): SedbukResolution {
  // ==================== 1. GC NUMBER LOOKUP ====================

  if (input.gcNumber) {
    const normalizedGC = normalizeGCNumber(input.gcNumber);
    const entry = (sedbukData.data as Record<string, SedbukEntry>)[normalizedGC];

    if (entry) {
      return {
        value: entry.sedbuk,
        band: entry.band as 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G',
        label: `${Math.round(entry.sedbuk * 100)}% SEDBUK`,
        source: "gc_lookup",
        confidence: "high",
        matchedBoiler: {
          make: entry.make,
          model: entry.model,
          gcNumber: normalizedGC,
          sedbuk: entry.sedbuk,
          band: entry.band,
        },
      };
    }
  }

  // ==================== 2. MAKE + MODEL LOOKUP ====================

  if (input.make && input.model) {
    const fuzzyMatch = findByMakeModel(input.make, input.model);

    if (fuzzyMatch) {
      return {
        value: fuzzyMatch.sedbuk,
        band: fuzzyMatch.band as 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G',
        label: `${Math.round(fuzzyMatch.sedbuk * 100)}% SEDBUK`,
        source: "model_lookup",
        confidence: "medium",
        matchedBoiler: {
          make: fuzzyMatch.make,
          model: fuzzyMatch.model,
          gcNumber: fuzzyMatch.gcNumber,
          sedbuk: fuzzyMatch.sedbuk,
          band: fuzzyMatch.band,
        },
      };
    }
  }

  // ==================== 3. HEAT-LOSS BAND FALLBACK ====================

  if (input.heatLossBand) {
    const efficiency = getEfficiencyFromHeatLossBand(input.heatLossBand);
    return {
      value: efficiency,
      label: `${Math.round(efficiency * 100)}% efficiency`,
      source: "heat_loss_band",
      confidence: "low",
    };
  }

  // ==================== 4. AGE BAND FALLBACK ====================

  if (input.ageBand) {
    const efficiency = getEfficiencyFromAgeBand(input.ageBand);
    return {
      value: efficiency,
      label: `${Math.round(efficiency * 100)}% efficiency`,
      source: "assumed",
      confidence: "low",
    };
  }

  // ==================== 5. DEFAULT FALLBACK ====================

  // Worst case: no data available, use conservative estimate
  const defaultEfficiency = 0.75; // 75% efficiency (typical older boiler)

  return {
    value: defaultEfficiency,
    label: `${Math.round(defaultEfficiency * 100)}% efficiency`,
    source: "assumed",
    confidence: "low",
  };
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Normalize GC number format
 * Handles variations like "47-116-30", "47 116 30", "47116030"
 * Returns the first matching format from the SEDBUK dataset
 */
function normalizeGCNumber(gcNumber: string): string {
  const data = sedbukData.data as Record<string, SedbukEntry>;
  
  // If already formatted with hyphens, try exact trimmed form first
  const trimmed = gcNumber.trim();
  if (trimmed.includes('-') && data[trimmed]) {
    return trimmed;
  }
  
  // Remove all non-digit characters
  const digitsOnly = gcNumber.replace(/\D/g, '');
  
  // Generate candidate formats
  const candidates: string[] = [];
  
  if (digitsOnly.length >= 7) {
    // Try first 7 digits: XX-XXX-XX
    candidates.push(`${digitsOnly.slice(0, 2)}-${digitsOnly.slice(2, 5)}-${digitsOnly.slice(5, 7)}`);
    
    // For 8+ digit strings, try last 7 digits (handles leading zeros/prefixes)
    if (digitsOnly.length > 7) {
      const lastSeven = digitsOnly.slice(-7);
      candidates.push(`${lastSeven.slice(0, 2)}-${lastSeven.slice(2, 5)}-${lastSeven.slice(5, 7)}`);
    }
  }
  
  // Return first candidate that exists in dataset
  for (const candidate of candidates) {
    if (data[candidate]) {
      return candidate;
    }
  }
  
  // If no match found, return the first candidate or original
  return candidates.length > 0 ? candidates[0] : gcNumber;
}

/**
 * Find boiler by make and model using fuzzy matching
 */
function findByMakeModel(make: string, model: string): (SedbukEntry & { gcNumber: string }) | null {
  const data = sedbukData.data as Record<string, SedbukEntry>;

  const normalizeMake = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const normalizeModel = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

  const targetMake = normalizeMake(make);
  const targetModel = normalizeModel(model);

  // Try exact match first
  for (const [gcNumber, entry] of Object.entries(data)) {
    if (
      normalizeMake(entry.make) === targetMake &&
      normalizeModel(entry.model) === targetModel
    ) {
      return { ...entry, gcNumber };
    }
  }

  // Try partial model match (e.g., "Greenstar 4000" matches "Greenstar 4000 25kW Combi")
  for (const [gcNumber, entry] of Object.entries(data)) {
    if (
      normalizeMake(entry.make) === targetMake &&
      normalizeModel(entry.model).includes(targetModel)
    ) {
      return { ...entry, gcNumber };
    }
  }

  return null;
}

/**
 * Get efficiency estimate from heat-loss band
 * (When EPC/SEDBUK not available, use property characteristics)
 */
function getEfficiencyFromHeatLossBand(band: "very_efficient" | "typical" | "poor"): number {
  switch (band) {
    case "very_efficient":
      return 0.90; // Modern well-insulated property with good boiler
    case "typical":
      return 0.80; // Average UK property
    case "poor":
      return 0.70; // Poor insulation, older boiler
    default:
      return 0.75; // Conservative default
  }
}

/**
 * Get efficiency estimate from property age band
 * (When no other data available)
 */
function getEfficiencyFromAgeBand(ageBand: string): number {
  // Map age band to typical boiler efficiency
  const ageBandEfficiency: Record<string, number> = {
    "2011_plus": 0.90,    // Modern properties likely have modern boilers
    "2001_2010": 0.88,    // Relatively recent
    "1981_2000": 0.82,    // Mixture of old and updated
    "1965_1980": 0.78,    // Older, may have been updated
    "1945_1964": 0.75,    // Likely older boilers
    "1919_1944": 0.72,    // Very old properties
    "pre_1919": 0.70,     // Historic properties
    "unknown": 0.75,      // Conservative default
  };

  return ageBandEfficiency[ageBand] || 0.75;
}

// ==================== EXPORTS ====================

export type { SedbukEntry };
